import {
    BufferTarget,
    CanvasSource,
    Mp4OutputFormat,
    Output,
    canEncodeVideo
} from "mediabunny";

const FRAME_DURATION = 1.8;
const FAST_FRAME_DURATION = 0.05625;
const KEYFRAME_INTERVAL = 60;
const BITRATE = 4000000;

const even = (value) => Math.ceil(value / 2) * 2;

/**
 * Whether the WebCodecs API can encode H.264 frames in this browser.
 *
 * @return {Boolean}
 */
export function isSupported() {
    return typeof VideoEncoder !== "undefined"
        && typeof createImageBitmap === "function";
}

/**
 * Encodes JPEG frame blobs into an H.264 MP4 entirely in the browser, so the
 * server receives a finished video instead of every individual frame. Every
 * frame is normalised to the size of the first one (animation frames are
 * captured at a lower scale), and fast frames get a shorter duration.
 *
 * @param {Array.<Blob>} frames
 * @param {Set.<Number>} fastFrameIndexes
 * @return {Promise.<Blob>}
 */
export async function encodeFrames(frames, fastFrameIndexes) {

    if (!frames.length) {
        throw new Error("No frames to encode");
    }

    const first = await createImageBitmap(frames[0]);
    const width = even(first.width);
    const height = even(first.height);
    first.close();

    if (!await canEncodeVideo("avc", { width, height, bitrate: BITRATE })) {
        throw new Error("H.264 encoding is not supported");
    }

    const target = new BufferTarget();
    const output = new Output({
        target,
        format: new Mp4OutputFormat({
            fastStart: "in-memory"
        })
    });

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");

    const source = new CanvasSource(canvas, {
        codec: "avc",
        bitrate: BITRATE,
        latencyMode: "quality"
    });
    output.addVideoTrack(source);

    await output.start();

    try {
        let timestamp = 0;

        for (const [index, frame] of frames.entries()) {
            const duration = fastFrameIndexes.has(index)
                ? FAST_FRAME_DURATION
                : FRAME_DURATION;

            const bitmap = await createImageBitmap(frame);
            context.drawImage(bitmap, 0, 0, width, height);
            bitmap.close();

            await source.add(timestamp, duration, {
                keyFrame: index % KEYFRAME_INTERVAL === 0
            });
            timestamp += duration;
        }

        await output.finalize();
    } catch (error) {
        await output.cancel().catch(() => {});
        throw error;
    }

    return new Blob([target.buffer], { type: "video/mp4" });

}
